import React from 'react';
import { useStore } from '../store';
import { MapPin, Navigation, Flag } from 'lucide-react';
import * as turf from '@turf/turf';
import type { RouteStatus } from '../types';

export const UserLocationPanel: React.FC = () => {
  const { users, routes, updateUserRouteStatus, settings } = useStore();

  const calculateUserRoute = React.useCallback((user: any): RouteStatus | null => {
    if (!user.currentPosition) return null;

    const userPoint = turf.point([user.currentPosition[0], user.currentPosition[1]]);
    const routeThreshold = settings.routeTolerance / 1000;
    const checkpointThreshold = settings.checkpointTolerance / 1000;

    const sortedRoutes = [...routes].sort((a, b) => {
      const aIndex = parseInt(a.name.replace(/[^0-9]/g, '')) || 0;
      const bIndex = parseInt(b.name.replace(/[^0-9]/g, '')) || 0;
      return aIndex - bIndex;
    });

    // アクティブなルートを優先的にチェック
    for (const route of sortedRoutes) {
      // ゴール済みのコースはスキップ
      if (user.completedRoutes?.includes(route.id)) {
        continue;
      }

      if (route.checkpoints.length < 2) continue;

      const sortedCheckpoints = [...route.checkpoints].sort((a, b) => {
        if (a.type === 'start') return -1;
        if (b.type === 'start') return 1;
        if (a.type === 'goal') return 1;
        if (b.type === 'goal') return -1;
        return 0;
      });

      const line = turf.lineString(sortedCheckpoints.map(cp => cp.position));
      const nearestPoint = turf.nearestPointOnLine(line, userPoint);
      const distanceToRoute = nearestPoint.properties.dist;

      // ゴール判定
      const goalPoint = sortedCheckpoints.find(cp => cp.type === 'goal');
      if (goalPoint) {
        const distanceToGoal = turf.distance(
          userPoint,
          turf.point(goalPoint.position),
          { units: 'kilometers' }
        );
        
        if (distanceToGoal <= checkpointThreshold) {
          return {
            name: route.name,
            color: '#9CA3AF', // グレー色
            onRoute: true,
            distanceToGoal: 0,
            distanceFromStart: route.totalDistance,
            priority: 0,
            completed: true,
            routeId: route.id // ルートIDを追加
          };
        }
      }

      // スタート判定
      const startPoint = sortedCheckpoints.find(cp => cp.type === 'start');
      if (startPoint) {
        const distanceToStart = turf.distance(
          userPoint,
          turf.point(startPoint.position),
          { units: 'kilometers' }
        );
        
        if (distanceToStart <= checkpointThreshold) {
          const totalDistance = turf.length(line, { units: 'kilometers' });
          const routeNumber = parseInt(route.name.replace(/[^0-9]/g, '')) || 0;
          return {
            name: route.name,
            color: route.color,
            onRoute: true,
            distanceToGoal: totalDistance,
            distanceFromStart: 0,
            priority: routeNumber,
            completed: false,
            routeId: route.id
          };
        }
      }

      if (distanceToRoute <= routeThreshold) {
        // ユーザーの進行方向を計算
        const userBearing = user.currentCourse || 0;
        
        // ルートを10mごとのセグメントに分割
        const totalDistance = turf.length(line, { units: 'kilometers' });
        const segments = Math.floor(totalDistance * 100); // 10mごとに分割
        const points = [];
        
        for (let i = 0; i <= segments; i++) {
          const distance = (i / segments) * totalDistance;
          points.push(turf.along(line, distance, { units: 'kilometers' }));
        }

        // 最も近い2つのセグメントポイントを見つける
        let minDistance = Infinity;
        let nearestSegment = null;
        let segmentIndex = 0;

        for (let i = 0; i < points.length - 1; i++) {
          const segment = turf.lineString([
            points[i].geometry.coordinates,
            points[i + 1].geometry.coordinates
          ]);
          const distance = turf.pointToLineDistance(userPoint, segment, { units: 'kilometers' });
          
          if (distance < minDistance) {
            minDistance = distance;
            nearestSegment = segment;
            segmentIndex = i;
          }
        }

        if (!nearestSegment) continue;

        // セグメントの方向を計算
        const segmentBearing = turf.bearing(
          turf.point(nearestSegment.geometry.coordinates[0]),
          turf.point(nearestSegment.geometry.coordinates[1])
        );

        // 進行方向とセグメントの方向の差を計算
        const bearingDiff = Math.abs(userBearing - segmentBearing);
        const isMovingForward = bearingDiff < 90 || bearingDiff > 270;

        // スタートからの距離を計算
        const startToUserLine = turf.lineSlice(
          turf.point(startPoint!.position),
          nearestPoint,
          line
        );
        const distanceFromStart = turf.length(startToUserLine, { units: 'kilometers' });

        // 残り距離を計算
        let distanceToGoal;
        if (isMovingForward) {
          // 前進している場合、現在地からゴールまでの距離
          const remainingLine = turf.lineSlice(
            nearestPoint,
            turf.point(goalPoint!.position),
            line
          );
          distanceToGoal = turf.length(remainingLine, { units: 'kilometers' });
        } else {
          // 後退している場合、現在地からスタートを経由してゴールまでの距離
          const toStartLine = turf.lineSlice(
            nearestPoint,
            turf.point(startPoint!.position),
            line
          );
          const startToGoalLine = turf.lineSlice(
            turf.point(startPoint!.position),
            turf.point(goalPoint!.position),
            line
          );
          distanceToGoal = turf.length(toStartLine, { units: 'kilometers' }) +
                          turf.length(startToGoalLine, { units: 'kilometers' });
        }

        const routeNumber = parseInt(route.name.replace(/[^0-9]/g, '')) || 0;
        return {
          name: route.name,
          color: route.color,
          onRoute: true,
          distanceToGoal,
          distanceFromStart,
          bearing: segmentBearing,
          priority: routeNumber,
          completed: false,
          routeId: route.id
        };
      }
    }

    // ゴール済みのコースの表示
    for (const route of sortedRoutes) {
      if (!user.completedRoutes?.includes(route.id)) continue;

      if (route.checkpoints.length < 2) continue;

      const sortedCheckpoints = [...route.checkpoints].sort((a, b) => {
        if (a.type === 'start') return -1;
        if (b.type === 'start') return 1;
        if (a.type === 'goal') return 1;
        if (b.type === 'goal') return -1;
        return 0;
      });

      const line = turf.lineString(sortedCheckpoints.map(cp => cp.position));
      const nearestPoint = turf.nearestPointOnLine(line, userPoint);
      const distanceToRoute = nearestPoint.properties.dist;

      if (distanceToRoute <= routeThreshold) {
        return {
          name: route.name,
          color: '#9CA3AF', // グレー色
          onRoute: true,
          distanceToGoal: 0,
          distanceFromStart: route.totalDistance,
          priority: 0,
          completed: true,
          routeId: route.id
        };
      }
    }

    return {
      name: 'コースアウト',
      color: '#FF0000',
      onRoute: false,
      distanceToGoal: -1,
      distanceFromStart: 0,
      priority: 0,
      completed: false,
      routeId: null
    };
  }, [routes, settings.routeTolerance, settings.checkpointTolerance]);

  React.useEffect(() => {
    const updateInterval = setInterval(() => {
      users.forEach(user => {
        if (user.currentPosition) {
          const newRouteStatus = calculateUserRoute(user);
          if (JSON.stringify(newRouteStatus) !== JSON.stringify(user.lastRouteStatus)) {
            updateUserRouteStatus(user.id, newRouteStatus);
          }
        }
      });
    }, 100);

    return () => clearInterval(updateInterval);
  }, [users, calculateUserRoute, updateUserRouteStatus]);

  return (
    <div className="bg-white p-4 rounded-lg shadow-lg w-80">
      <h2 className="text-lg font-bold mb-4">ユーザー位置情報</h2>
      <div className="space-y-4">
        {users.map((user) => {
          const routeStatus = user.lastRouteStatus;
          
          return (
            <div key={user.id} className="p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium">{user.name}</span>
                {user.currentPosition ? (
                  <span className="text-green-500 text-sm">オンライン</span>
                ) : (
                  <span className="text-gray-400 text-sm">オフライン</span>
                )}
              </div>
              {user.currentPosition ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <MapPin className="w-4 h-4" />
                    <span>
                      {user.currentPosition[1].toFixed(6)}, {user.currentPosition[0].toFixed(6)}
                    </span>
                  </div>
                  {user.currentSpeed !== undefined && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Navigation className="w-4 h-4" />
                      <span>{user.currentSpeed.toFixed(1)} km/h</span>
                    </div>
                  )}
                  {routeStatus && (
                    <div className={`mt-2 p-2 rounded ${
                      routeStatus.onRoute ? 'bg-opacity-20' : 'bg-red-100'
                    }`} style={{ 
                      backgroundColor: routeStatus.onRoute ? `${routeStatus.color}20` : undefined 
                    }}>
                      <div className="flex items-center justify-between">
                        <div className="text-sm font-medium" style={{ 
                          color: routeStatus.onRoute ? routeStatus.color : '#DC2626'
                        }}>
                          {routeStatus.name}
                          {routeStatus.completed && (
                            <span className="ml-2 text-xs text-gray-500">
                              (完了)
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1 text-sm text-gray-600">
                          <Flag className="w-4 h-4" />
                          <span>
                            {routeStatus.onRoute && !routeStatus.completed
                              ? `残り ${routeStatus.distanceToGoal.toFixed(3)}km`
                              : '---'
                            }
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-sm text-gray-400">
                  位置情報が取得できていません
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};